import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, X, Image as ImageIcon, Download, RotateCw } from 'lucide-react';
import { useSelf, userKeys, type User } from '../../services/queries/userQueries';
import {
  isBypassModeEnabled,
  setBypassMode,
  subscribeToBypassModeChanges,
  getCurrentModeLabel,
  getCurrentModeDescription
} from '../../services/featureFlagService';
import {
  fetchSettings,
  updateThemeSettings,
  updateDisplaySettings,
  updateNotificationSettings,
  updateSecuritySettings,
  updateSystemSettings,
  resetSettingsToDefaults,
  exportSettings,
  importSettings,
  type ThemeSettings,
  type DisplaySettings,
  type NotificationSettings,
  type SecuritySettings,
  type SystemSettings,
  type ThemeMode,
  type ColorScheme,
  type DateFormat,
  type TimeFormat
} from '../../services/api/uiConfigurationApi';
import { StandardLoadingState, StandardErrorState } from '../standard';

// Branding settings interface
interface BrandingSettings {
  companyName: string;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
}

const BRANDING_STORAGE_KEY = 'branding_settings';

const DEFAULT_BRANDING: BrandingSettings = {
  companyName: '',
  logoUrl: '',
  faviconUrl: '',
  primaryColor: '#3b82f6'
};

type SettingsTab = 'theme' | 'display' | 'notifications' | 'security' | 'system';

// ===========================
// TAB COMPONENTS
// ===========================

interface ThemeTabProps {
  theme: ThemeSettings;
  onUpdate: (updates: Partial<ThemeSettings>) => void;
  onReset: () => void;
  isUpdating: boolean;
  isResetting: boolean;
}

const ThemeTab: React.FC<ThemeTabProps> = ({ theme, onUpdate, onReset, isUpdating, isResetting }) => {
  // Branding state
  const [brandingSettings, setBrandingSettings] = useState<BrandingSettings>(() => {
    try {
      const saved = localStorage.getItem(BRANDING_STORAGE_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_BRANDING;
    } catch {
      return DEFAULT_BRANDING;
    }
  });
  const [hasUnsavedBrandingChanges, setHasUnsavedBrandingChanges] = useState(false);
  const [brandingSaveMessage, setBrandingSaveMessage] = useState<string | null>(null);

  // Branding handlers
  const updateBrandingSetting = (key: keyof BrandingSettings, value: string) => {
    setBrandingSettings(prev => ({ ...prev, [key]: value }));
    setHasUnsavedBrandingChanges(true);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      updateBrandingSetting('logoUrl', reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFaviconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      updateBrandingSetting('faviconUrl', reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveBranding = () => {
    localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(brandingSettings));
    setHasUnsavedBrandingChanges(false);

    // Apply favicon immediately
    if (brandingSettings.faviconUrl) {
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = brandingSettings.faviconUrl;
    }

    // Apply company name to document title
    if (brandingSettings.companyName) {
      document.title = `${brandingSettings.companyName} - Product Hub`;
    }

    // Dispatch event for other components to react
    window.dispatchEvent(new CustomEvent('branding-updated', { detail: brandingSettings }));

    setBrandingSaveMessage('Branding settings saved successfully!');
    setTimeout(() => setBrandingSaveMessage(null), 3000);
  };

  const colorSchemes: Array<{ value: ColorScheme; label: string; color: string }> = [
    { value: 'blue', label: 'Professional Blue', color: '#2563eb' },
    { value: 'green', label: 'Success Green', color: '#16a34a' },
    { value: 'purple', label: 'Creative Purple', color: '#9333ea' },
    { value: 'red', label: 'Alert Red', color: '#dc2626' },
    { value: 'orange', label: 'Warm Orange', color: '#ea580c' },
    { value: 'teal', label: 'Calm Teal', color: '#0d9488' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Theme Settings</h4>
        <button
          className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          onClick={onReset}
          disabled={isResetting}
        >
          {isResetting ? (
            <>
              <RotateCw className="w-4 h-4 inline mr-1 animate-spin" />
              Resetting...
            </>
          ) : (
            <>
              <RotateCw className="w-4 h-4 inline mr-1" />
              Reset Theme
            </>
          )}
        </button>
      </div>

      <div className="space-y-6">
        {/* Theme Mode */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Theme Mode
          </label>
          <div className="flex gap-4">
            {(['light', 'dark', 'auto'] as ThemeMode[]).map(mode => (
              <label key={mode} className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                <input
                  type="radio"
                  name="theme-mode"
                  value={mode}
                  checked={theme.mode === mode}
                  onChange={(e) => onUpdate({ mode: e.target.value as ThemeMode })}
                  disabled={isUpdating}
                  className="w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-purple-500"
                />
                <span className="capitalize">{mode}</span>
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Auto mode switches based on system preferences
          </p>
        </div>

        {/* Color Scheme */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Color Scheme
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {colorSchemes.map(scheme => (
              <div
                key={scheme.value}
                onClick={() => onUpdate({ colorScheme: scheme.value })}
                className={`p-3 rounded-lg cursor-pointer flex items-center gap-3 transition-all ${
                  theme.colorScheme === scheme.value
                    ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800'
                    : 'border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                } bg-white dark:bg-gray-700`}
                style={{
                  borderColor: theme.colorScheme === scheme.value ? scheme.color : undefined,
                  boxShadow: theme.colorScheme === scheme.value ? `0 0 0 2px ${scheme.color}` : undefined
                }}
              >
                <div
                  className="w-6 h-6 rounded-full"
                  style={{ background: scheme.color }}
                />
                <span className="text-sm font-medium text-gray-900 dark:text-white">{scheme.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Font Size */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Font Size
          </label>
          <div className="flex gap-4">
            {(['small', 'medium', 'large'] as const).map(size => (
              <label key={size} className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                <input
                  type="radio"
                  name="font-size"
                  value={size}
                  checked={theme.fontSize === size}
                  onChange={(e) => onUpdate({ fontSize: e.target.value as 'small' | 'medium' | 'large' })}
                  disabled={isUpdating}
                  className="w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-purple-500"
                />
                <span className="capitalize">{size}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Logo & Branding Section */}
        <div className="space-y-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
              Logo & Branding
            </h4>
            {hasUnsavedBrandingChanges && (
              <span className="text-sm text-amber-600 dark:text-amber-400">Unsaved changes</span>
            )}
          </div>

          {/* Success Message */}
          {brandingSaveMessage && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-200">{brandingSaveMessage}</p>
            </div>
          )}

          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={brandingSettings.companyName}
              onChange={(e) => updateBrandingSetting('companyName', e.target.value)}
              placeholder="Enter your company name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              This name will appear in the header and page title
            </p>
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Company Logo
            </label>
            <div className="flex items-start gap-4">
              {/* Logo Preview */}
              <div className="flex-shrink-0">
                {brandingSettings.logoUrl ? (
                  <div className="relative">
                    <img
                      src={brandingSettings.logoUrl}
                      alt="Company Logo"
                      className="h-20 w-20 object-contain rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-2"
                    />
                    <button
                      onClick={() => updateBrandingSetting('logoUrl', '')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div className="h-20 w-20 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Upload Controls */}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer text-sm"
                >
                  <Upload className="h-4 w-4" />
                  Upload Logo
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Recommended: Square image, PNG format, 200x200px minimum
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Or paste image URL:
                </p>
                <input
                  type="url"
                  value={brandingSettings.logoUrl}
                  onChange={(e) => updateBrandingSetting('logoUrl', e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="w-full mt-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Favicon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Favicon
            </label>
            <div className="flex items-start gap-4">
              {/* Favicon Preview */}
              <div className="flex-shrink-0">
                {brandingSettings.faviconUrl ? (
                  <div className="relative">
                    <img
                      src={brandingSettings.faviconUrl}
                      alt="Favicon"
                      className="h-12 w-12 object-contain rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-1"
                    />
                    <button
                      onClick={() => updateBrandingSetting('faviconUrl', '')}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ) : (
                  <div className="h-12 w-12 rounded border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                    <ImageIcon className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Upload Controls */}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/x-icon,image/png,image/ico,.ico"
                  onChange={handleFaviconUpload}
                  className="hidden"
                  id="favicon-upload"
                />
                <label
                  htmlFor="favicon-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer text-sm"
                >
                  <Upload className="h-3 w-3" />
                  Upload Favicon
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Recommended: ICO or PNG, 32x32px or 64x64px
                </p>
                <input
                  type="url"
                  value={brandingSettings.faviconUrl}
                  onChange={(e) => updateBrandingSetting('faviconUrl', e.target.value)}
                  placeholder="https://example.com/favicon.ico"
                  className="w-full mt-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Primary Brand Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Primary Brand Color
            </label>
            <div className="flex items-center gap-4">
              <input
                type="color"
                value={brandingSettings.primaryColor}
                onChange={(e) => updateBrandingSetting('primaryColor', e.target.value)}
                className="h-10 w-20 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
              />
              <input
                type="text"
                value={brandingSettings.primaryColor}
                onChange={(e) => updateBrandingSetting('primaryColor', e.target.value)}
                placeholder="#3b82f6"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <div
                className="h-10 w-10 rounded-lg border border-gray-300 dark:border-gray-600"
                style={{ backgroundColor: brandingSettings.primaryColor }}
                title="Color preview"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              This color will be used for primary buttons and accents
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSaveBranding}
              className={`px-6 py-2 rounded-lg transition-colors ${
                hasUnsavedBrandingChanges
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
              disabled={!hasUnsavedBrandingChanges}
            >
              Save Branding
            </button>
          </div>
        </div>

        {/* Toggle Options */}
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={theme.compactMode}
              onChange={(e) => onUpdate({ compactMode: e.target.checked })}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Compact Mode</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Reduce spacing and padding for denser layouts
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={theme.highContrast}
              onChange={(e) => onUpdate({ highContrast: e.target.checked })}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">High Contrast</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Increase color contrast for better accessibility
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={theme.reducedMotion}
              onChange={(e) => onUpdate({ reducedMotion: e.target.checked })}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Reduced Motion</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Minimize animations and transitions
              </div>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};

interface DisplayTabProps {
  display: DisplaySettings;
  onUpdate: (updates: Partial<DisplaySettings>) => void;
  onReset: () => void;
  isUpdating: boolean;
  isResetting: boolean;
}

const DisplayTab: React.FC<DisplayTabProps> = ({ display, onUpdate, onReset, isUpdating, isResetting }) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Display Settings</h4>
        <button
          className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          onClick={onReset}
          disabled={isResetting}
        >
          {isResetting ? (
            <>
              <RotateCw className="w-4 h-4 inline mr-1 animate-spin" />
              Resetting...
            </>
          ) : (
            <>
              <RotateCw className="w-4 h-4 inline mr-1" />
              Reset Display
            </>
          )}
        </button>
      </div>

      <div className="space-y-6">
        {/* Date Format */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Date Format
          </label>
          <select
            value={display.dateFormat}
            onChange={(e) => onUpdate({ dateFormat: e.target.value as DateFormat })}
            disabled={isUpdating}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2025)</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2025)</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD (2025-12-31)</option>
            <option value="MMM DD, YYYY">MMM DD, YYYY (Dec 31, 2025)</option>
            <option value="DD MMM YYYY">DD MMM YYYY (31 Dec 2025)</option>
          </select>
        </div>

        {/* Time Format */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Time Format
          </label>
          <div className="flex gap-4">
            {(['12h', '24h'] as TimeFormat[]).map(format => (
              <label key={format} className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                <input
                  type="radio"
                  name="time-format"
                  value={format}
                  checked={display.timeFormat === format}
                  onChange={(e) => onUpdate({ timeFormat: e.target.value as TimeFormat })}
                  disabled={isUpdating}
                  className="w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-purple-500"
                />
                <span>{format === '12h' ? '12-hour (3:30 PM)' : '24-hour (15:30)'}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Timezone */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Timezone
          </label>
          <select
            value={display.timezone}
            onChange={(e) => onUpdate({ timezone: e.target.value })}
            disabled={isUpdating}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="America/New_York">Eastern Time (US & Canada)</option>
            <option value="America/Chicago">Central Time (US & Canada)</option>
            <option value="America/Denver">Mountain Time (US & Canada)</option>
            <option value="America/Los_Angeles">Pacific Time (US & Canada)</option>
            <option value="Europe/London">London</option>
            <option value="Europe/Paris">Paris</option>
            <option value="Asia/Tokyo">Tokyo</option>
            <option value="Australia/Sydney">Sydney</option>
          </select>
        </div>

        {/* Number Format */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Number Format
          </label>
          <select
            value={display.numberFormat}
            onChange={(e) => onUpdate({ numberFormat: e.target.value as any })}
            disabled={isUpdating}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="en-US">1,234.56 (US/UK)</option>
            <option value="de-DE">1.234,56 (German)</option>
            <option value="fr-FR">1 234,56 (French)</option>
            <option value="es-ES">1.234,56 (Spanish)</option>
          </select>
        </div>

        {/* Currency Symbol */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Currency Symbol
          </label>
          <input
            type="text"
            value={display.currencySymbol}
            onChange={(e) => onUpdate({ currencySymbol: e.target.value })}
            disabled={isUpdating}
            maxLength={3}
            className="w-24 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Symbol used for currency values (e.g., $, €, £)
          </p>
        </div>

        {/* Items Per Page */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Items Per Page
          </label>
          <input
            type="number"
            value={display.itemsPerPage}
            onChange={(e) => onUpdate({ itemsPerPage: parseInt(e.target.value) })}
            disabled={isUpdating}
            min={10}
            max={100}
            step={5}
            className="w-32 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Default number of items to show in paginated lists
          </p>
        </div>

        {/* Default View */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Default View
          </label>
          <div className="flex gap-4">
            {(['grid', 'list', 'kanban'] as const).map(view => (
              <label key={view} className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                <input
                  type="radio"
                  name="default-view"
                  value={view}
                  checked={display.defaultView === view}
                  onChange={(e) => onUpdate({ defaultView: e.target.value as 'grid' | 'list' | 'kanban' })}
                  disabled={isUpdating}
                  className="w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-purple-500"
                />
                <span className="capitalize">{view}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Show Grid Lines */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={display.showGridLines}
            onChange={(e) => onUpdate({ showGridLines: e.target.checked })}
            disabled={isUpdating}
            className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
          />
          <div>
            <div className="font-semibold text-gray-900 dark:text-white">Show Grid Lines</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Display grid lines in table views
            </div>
          </div>
        </label>
      </div>
    </div>
  );
};

interface NotificationsTabProps {
  notifications: NotificationSettings;
  onUpdate: (updates: Partial<NotificationSettings>) => void;
  onReset: () => void;
  isUpdating: boolean;
  isResetting: boolean;
}

const NotificationsTab: React.FC<NotificationsTabProps> = ({ notifications, onUpdate, onReset, isUpdating, isResetting }) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Notification Settings</h4>
        <button
          className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          onClick={onReset}
          disabled={isResetting}
        >
          {isResetting ? (
            <>
              <RotateCw className="w-4 h-4 inline mr-1 animate-spin" />
              Resetting...
            </>
          ) : (
            <>
              <RotateCw className="w-4 h-4 inline mr-1" />
              Reset Notifications
            </>
          )}
        </button>
      </div>

      <div className="space-y-6">
        {/* Notification Channels */}
        <div>
          <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Notification Channels</h5>
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.emailNotifications}
                onChange={(e) => onUpdate({ emailNotifications: e.target.checked })}
                disabled={isUpdating}
                className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
              />
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">📧 Email Notifications</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Receive notifications via email
                </div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.pushNotifications}
                onChange={(e) => onUpdate({ pushNotifications: e.target.checked })}
                disabled={isUpdating}
                className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
              />
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">📱 Push Notifications</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Receive push notifications on mobile devices
                </div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.desktopNotifications}
                onChange={(e) => onUpdate({ desktopNotifications: e.target.checked })}
                disabled={isUpdating}
                className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
              />
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">🖥️ Desktop Notifications</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Show browser desktop notifications
                </div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.soundEnabled}
                onChange={(e) => onUpdate({ soundEnabled: e.target.checked })}
                disabled={isUpdating}
                className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
              />
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">🔊 Sound</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Play sound for notifications
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Notification Types */}
        <div>
          <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Notification Types</h5>
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.notifyOnTaskAssignment}
                onChange={(e) => onUpdate({ notifyOnTaskAssignment: e.target.checked })}
                disabled={isUpdating}
                className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
              />
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">Task Assignment</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  When a task is assigned to you
                </div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.notifyOnTaskCompletion}
                onChange={(e) => onUpdate({ notifyOnTaskCompletion: e.target.checked })}
                disabled={isUpdating}
                className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
              />
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">Task Completion</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  When a task you're involved with is completed
                </div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.notifyOnMention}
                onChange={(e) => onUpdate({ notifyOnMention: e.target.checked })}
                disabled={isUpdating}
                className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
              />
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">Mentions</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  When someone mentions you (@username)
                </div>
              </div>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.notifyOnComment}
                onChange={(e) => onUpdate({ notifyOnComment: e.target.checked })}
                disabled={isUpdating}
                className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
              />
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">Comments</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  When someone comments on your items
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Daily Digest */}
        <div>
          <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Daily Digest</h5>
          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={notifications.dailyDigestEnabled}
              onChange={(e) => onUpdate({ dailyDigestEnabled: e.target.checked })}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Enable Daily Digest</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Receive a summary of daily activity
              </div>
            </div>
          </label>

          {notifications.dailyDigestEnabled && (
            <div className="ml-7">
              <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
                Digest Time
              </label>
              <input
                type="time"
                value={notifications.dailyDigestTime}
                onChange={(e) => onUpdate({ dailyDigestTime: e.target.value })}
                disabled={isUpdating}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface SecurityTabProps {
  security: SecuritySettings;
  onUpdate: (updates: Partial<SecuritySettings>) => void;
  onReset: () => void;
  isUpdating: boolean;
  isResetting: boolean;
}

const SecurityTab: React.FC<SecurityTabProps> = ({ security, onUpdate, onReset, isUpdating, isResetting }) => {
  const [newIpAddress, setNewIpAddress] = useState('');

  const handleAddIpAddress = () => {
    if (newIpAddress && !security.allowedIpAddresses.includes(newIpAddress)) {
      onUpdate({
        allowedIpAddresses: [...security.allowedIpAddresses, newIpAddress]
      });
      setNewIpAddress('');
    }
  };

  const handleRemoveIpAddress = (ip: string) => {
    onUpdate({
      allowedIpAddresses: security.allowedIpAddresses.filter(addr => addr !== ip)
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Security Settings</h4>
        <button
          className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          onClick={onReset}
          disabled={isResetting}
        >
          {isResetting ? (
            <>
              <RotateCw className="w-4 h-4 inline mr-1 animate-spin" />
              Resetting...
            </>
          ) : (
            <>
              <RotateCw className="w-4 h-4 inline mr-1" />
              Reset Security
            </>
          )}
        </button>
      </div>

      <div className="space-y-6">
        {/* Session Timeout */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Session Timeout (minutes)
          </label>
          <input
            type="number"
            value={security.sessionTimeout}
            onChange={(e) => onUpdate({ sessionTimeout: parseInt(e.target.value) })}
            disabled={isUpdating}
            min={5}
            max={1440}
            className="w-32 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Automatically log out users after this period of inactivity
          </p>
        </div>

        {/* Password Expiry */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Password Expiry (days)
          </label>
          <input
            type="number"
            value={security.passwordExpiryDays}
            onChange={(e) => onUpdate({ passwordExpiryDays: parseInt(e.target.value) })}
            disabled={isUpdating}
            min={0}
            max={365}
            className="w-32 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Require password change after this many days (0 = never expires)
          </p>
        </div>

        {/* Lockout Settings */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Lockout Threshold
          </label>
          <input
            type="number"
            value={security.lockoutThreshold}
            onChange={(e) => onUpdate({ lockoutThreshold: parseInt(e.target.value) })}
            disabled={isUpdating}
            min={3}
            max={10}
            className="w-32 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Number of failed login attempts before account lockout
          </p>
        </div>

        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Lockout Duration (minutes)
          </label>
          <input
            type="number"
            value={security.lockoutDuration}
            onChange={(e) => onUpdate({ lockoutDuration: parseInt(e.target.value) })}
            disabled={isUpdating}
            min={5}
            max={60}
            className="w-32 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            How long to lock the account after exceeding threshold
          </p>
        </div>

        {/* Security Features */}
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={security.mfaEnabled}
              onChange={(e) => onUpdate({ mfaEnabled: e.target.checked })}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Enable Multi-Factor Authentication (MFA)</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Require users to verify identity with a second factor
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={security.loginNotifications}
              onChange={(e) => onUpdate({ loginNotifications: e.target.checked })}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Login Notifications</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Notify users of new login activity
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={security.requirePasswordChange}
              onChange={(e) => onUpdate({ requirePasswordChange: e.target.checked })}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Require Password Change on Next Login</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Force all users to change their password on next login
              </div>
            </div>
          </label>
        </div>

        {/* IP Whitelist */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Allowed IP Addresses
          </label>
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Restrict access to specific IP addresses (empty = all IPs allowed)
          </p>

          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newIpAddress}
              onChange={(e) => setNewIpAddress(e.target.value)}
              placeholder="192.168.1.100"
              className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <button
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              onClick={handleAddIpAddress}
              disabled={!newIpAddress || isUpdating}
            >
              Add IP
            </button>
          </div>

          {security.allowedIpAddresses.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {security.allowedIpAddresses.map(ip => (
                <div
                  key={ip}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white"
                >
                  <span>{ip}</span>
                  <button
                    onClick={() => handleRemoveIpAddress(ip)}
                    disabled={isUpdating}
                    className="text-red-500 hover:text-red-700 dark:hover:text-red-400 font-bold"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface SystemTabProps {
  system: SystemSettings;
  onUpdate: (updates: Partial<SystemSettings>) => void;
  onReset: () => void;
  isUpdating: boolean;
  isResetting: boolean;
}

const SystemTab: React.FC<SystemTabProps> = ({ system, onUpdate, onReset, isUpdating, isResetting }) => {
  const [mvpBypassEnabled, setMvpBypassEnabled] = useState(isBypassModeEnabled);

  // Subscribe to bypass mode changes
  useEffect(() => {
    const unsubscribe = subscribeToBypassModeChanges((enabled) => {
      setMvpBypassEnabled(enabled);
    });
    return unsubscribe;
  }, []);

  const handleToggleMVPMode = () => {
    const newState = !mvpBypassEnabled;
    setBypassMode(newState);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">System Settings</h4>
        <button
          className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          onClick={onReset}
          disabled={isResetting}
        >
          {isResetting ? (
            <>
              <RotateCw className="w-4 h-4 inline mr-1 animate-spin" />
              Resetting...
            </>
          ) : (
            <>
              <RotateCw className="w-4 h-4 inline mr-1" />
              Reset System
            </>
          )}
        </button>
      </div>

      <div className="space-y-6">
        {/* Language */}
        <div>
          <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
            Language
          </label>
          <select
            value={system.language}
            onChange={(e) => onUpdate({ language: e.target.value })}
            disabled={isUpdating}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="en-US">English (US)</option>
            <option value="en-GB">English (UK)</option>
            <option value="es-ES">Español (Spanish)</option>
            <option value="fr-FR">Français (French)</option>
            <option value="de-DE">Deutsch (German)</option>
            <option value="ja-JP">日本語 (Japanese)</option>
            <option value="zh-CN">中文 (Chinese)</option>
          </select>
        </div>

        {/* Auto-Save */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={system.autoSaveEnabled}
              onChange={(e) => onUpdate({ autoSaveEnabled: e.target.checked })}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">Enable Auto-Save</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Automatically save changes at regular intervals
              </div>
            </div>
          </label>

          {system.autoSaveEnabled && (
            <div className="ml-7">
              <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
                Auto-Save Interval (seconds)
              </label>
              <input
                type="number"
                value={system.autoSaveInterval}
                onChange={(e) => onUpdate({ autoSaveInterval: parseInt(e.target.value) })}
                disabled={isUpdating}
                min={10}
                max={300}
                className="w-32 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          )}
        </div>

        {/* Backup Settings */}
        <div>
          <h5 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Backup Settings</h5>

          <div className="mb-4">
            <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
              Backup Frequency
            </label>
            <div className="flex gap-4">
              {(['daily', 'weekly', 'monthly'] as const).map(freq => (
                <label key={freq} className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="backup-frequency"
                    value={freq}
                    checked={system.backupFrequency === freq}
                    onChange={(e) => onUpdate({ backupFrequency: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                    disabled={isUpdating}
                    className="w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-purple-500"
                  />
                  <span className="capitalize">{freq}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block mb-2 font-semibold text-gray-900 dark:text-white">
              Backup Time
            </label>
            <input
              type="time"
              value={system.backupTime}
              onChange={(e) => onUpdate({ backupTime: e.target.value })}
              disabled={isUpdating}
              className="p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Time of day to run automated backups
            </p>
          </div>
        </div>

        {/* MVP Feature Restrictions Toggle */}
        <div className={`p-4 rounded-lg border-2 ${
          mvpBypassEnabled
            ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
            : 'bg-orange-50 dark:bg-orange-900/20 border-orange-500'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xl">{mvpBypassEnabled ? '🔓' : '🔒'}</span>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    MVP Feature Restrictions
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                    mvpBypassEnabled
                      ? 'bg-green-500 text-white'
                      : 'bg-orange-500 text-white'
                  }`}>
                    {getCurrentModeLabel()}
                  </span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {getCurrentModeDescription()}
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={mvpBypassEnabled}
                onChange={handleToggleMVPMode}
                className="sr-only peer"
              />
              <div className={`w-11 h-6 rounded-full peer peer-focus:outline-none peer-focus:ring-4 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
                mvpBypassEnabled
                  ? 'bg-green-500 peer-focus:ring-green-300'
                  : 'bg-gray-300 dark:bg-gray-600 peer-focus:ring-gray-300 dark:peer-focus:ring-gray-700'
              }`}></div>
            </label>
          </div>

          {/* Feature Summary */}
          <div className={`mt-3 pt-3 border-t grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs ${
            mvpBypassEnabled ? 'border-green-300 dark:border-green-700' : 'border-orange-300 dark:border-orange-700'
          }`}>
            {[
              'Credit Management',
              'Credit Block',
              'Leads & Contacts',
              'Opportunities',
              'Reports & Analytics',
              'Advanced Admin'
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-1.5">
                <span className={mvpBypassEnabled ? 'text-green-600' : 'text-gray-400'}>
                  {mvpBypassEnabled ? '✓' : '✗'}
                </span>
                <span className={mvpBypassEnabled ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400'}>
                  {feature}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* System Features */}
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={system.debugMode}
              onChange={(e) => onUpdate({ debugMode: e.target.checked })}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">🐛 Debug Mode</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Enable verbose logging for troubleshooting (not recommended for production)
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={system.analyticsEnabled}
              onChange={(e) => onUpdate({ analyticsEnabled: e.target.checked })}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">📊 Analytics</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Collect anonymous usage analytics to improve the application
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={system.errorReportingEnabled}
              onChange={(e) => onUpdate({ errorReportingEnabled: e.target.checked })}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">🚨 Error Reporting</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Automatically report errors to help identify and fix issues
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={system.maintenanceMode}
              onChange={(e) => {
                if (e.target.checked) {
                  if (window.confirm('Enable maintenance mode? This will restrict access for non-admin users.')) {
                    onUpdate({ maintenanceMode: true });
                  }
                } else {
                  onUpdate({ maintenanceMode: false });
                }
              }}
              disabled={isUpdating}
              className="mt-1 w-4 h-4 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500"
            />
            <div>
              <div className="font-semibold text-gray-900 dark:text-white">🔧 Maintenance Mode</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Restrict application access to administrators only
              </div>
            </div>
          </label>
        </div>

        {system.maintenanceMode && (
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <strong className="text-yellow-800 dark:text-yellow-200">⚠️ Maintenance Mode Active</strong>
            <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
              Only system administrators can access the application. Regular users will see a maintenance message.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ===========================
// MODAL COMPONENTS
// ===========================

interface ExportModalProps {
  onClose: () => void;
  onExport: () => void;
  isExporting: boolean;
}

const ExportModal: React.FC<ExportModalProps> = ({ onClose, onExport, isExporting }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-[90%] shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Export Settings</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          This will export all application settings as a JSON file. You can use this for backup or to import into another instance.
        </p>

        <div className="flex gap-3 justify-end">
          <button
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            onClick={onClose}
            disabled={isExporting}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors"
            onClick={onExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <Download className="w-4 h-4 inline mr-1 animate-pulse" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 inline mr-1" />
                Export Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

interface ImportModalProps {
  onClose: () => void;
  onImport: (file: File) => void;
  isImporting: boolean;
  error?: Error | null;
}

const ImportModal: React.FC<ImportModalProps> = ({ onClose, onImport, isImporting, error }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = () => {
    if (selectedFile) {
      onImport(selectedFile);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-[90%] shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Import Settings</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Import settings from a previously exported JSON file. This will overwrite all current settings.
        </p>

        {error && (
          <div className="p-3 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <strong className="text-red-800 dark:text-red-200">Import Failed:</strong>
            <span className="text-red-700 dark:text-red-300 ml-1">{error.message}</span>
          </div>
        )}

        <div className="mb-6">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
          >
            📁 Choose File
          </button>
          {selectedFile && (
            <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded text-sm text-gray-900 dark:text-white">
              Selected: {selectedFile.name}
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end">
          <button
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            onClick={onClose}
            disabled={isImporting}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            onClick={handleImport}
            disabled={!selectedFile || isImporting}
          >
            {isImporting ? (
              <>
                <Upload className="w-4 h-4 inline mr-1 animate-pulse" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 inline mr-1" />
                Import Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===========================
// MAIN COMPONENT
// ===========================

const ThemeSettingsManager: React.FC = () => {
  const queryClient = useQueryClient();

  // Get current user
  const { data: currentUser } = useSelf();

  // Helper to get current user name (computed fresh each time to avoid closure issues)
  const getCurrentUserName = (): string => {
    if (currentUser) {
      return `${currentUser.firstName} ${currentUser.lastName}`.trim() || currentUser.email || 'user_001';
    }
    // Fallback: try to get from query cache
    const cachedUser = queryClient.getQueryData<User>([...userKeys.all, 'self']);
    if (cachedUser) {
      return `${cachedUser.firstName} ${cachedUser.lastName}`.trim() || cachedUser.email || 'user_001';
    }
    return 'user_001';
  };

  // State
  const [activeTab, setActiveTab] = useState<SettingsTab>('theme');
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Fetch settings
  const { data: settingsResponse, isLoading, error } = useQuery({
    queryKey: ['settings'],
    queryFn: fetchSettings,
    staleTime: 60 * 1000
  });

  const settings = settingsResponse?.data;

  // Mutations
  const updateThemeMutation = useMutation({
    mutationFn: (updates: Partial<ThemeSettings>) => updateThemeSettings(updates, getCurrentUserName()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    }
  });

  const updateDisplayMutation = useMutation({
    mutationFn: (updates: Partial<DisplaySettings>) => updateDisplaySettings(updates, getCurrentUserName()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    }
  });

  const updateNotificationsMutation = useMutation({
    mutationFn: (updates: Partial<NotificationSettings>) => updateNotificationSettings(updates, getCurrentUserName()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    }
  });

  const updateSecurityMutation = useMutation({
    mutationFn: (updates: Partial<SecuritySettings>) => updateSecuritySettings(updates, getCurrentUserName()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    }
  });

  const updateSystemMutation = useMutation({
    mutationFn: (updates: Partial<SystemSettings>) => updateSystemSettings(updates, getCurrentUserName()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    }
  });

  const resetMutation = useMutation({
    mutationFn: () => resetSettingsToDefaults(getCurrentUserName()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    }
  });

  const exportMutation = useMutation({
    mutationFn: exportSettings
  });

  const importMutation = useMutation({
    mutationFn: (settingsJson: string) => importSettings(settingsJson, getCurrentUserName()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setShowImportModal(false);
    }
  });

  // Handlers
  const handleUpdateTheme = (updates: Partial<ThemeSettings>) => {
    updateThemeMutation.mutate(updates);
  };

  const handleUpdateDisplay = (updates: Partial<DisplaySettings>) => {
    updateDisplayMutation.mutate(updates);
  };

  const handleUpdateNotifications = (updates: Partial<NotificationSettings>) => {
    updateNotificationsMutation.mutate(updates);
  };

  const handleUpdateSecurity = (updates: Partial<SecuritySettings>) => {
    updateSecurityMutation.mutate(updates);
  };

  const handleUpdateSystem = (updates: Partial<SystemSettings>) => {
    updateSystemMutation.mutate(updates);
  };

  const handleReset = (category?: SettingsTab) => {
    const confirmMessage = category
      ? `Reset ${category} settings to defaults?`
      : 'Reset ALL settings to defaults? This cannot be undone!';

    if (window.confirm(confirmMessage)) {
      resetMutation.mutate();
    }
  };

  const handleExport = async () => {
    const result = await exportMutation.mutateAsync();

    // Create download
    const blob = new Blob([result.data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `settings-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setShowExportModal(false);
  };

  const handleImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      importMutation.mutate(content);
    };
    reader.readAsText(file);
  };

  // Loading state
  if (isLoading) {
    return <StandardLoadingState message="Loading settings..." />;
  }

  // Error state
  if (error || !settings) {
    return (
      <StandardErrorState
        title="Failed to load settings"
        message="Please try again or contact support if the problem persists."
      />
    );
  }

  const tabs = [
    { id: 'theme' as const, label: '🎨 Theme' },
    { id: 'display' as const, label: '📊 Display' },
    { id: 'notifications' as const, label: '🔔 Notifications' },
    { id: 'security' as const, label: '🔒 Security' },
    { id: 'system' as const, label: '⚙️ System' }
  ];

  return (
    <div className="space-y-6">
      {/* Developer Indicator */}
      {process.env.NODE_ENV === 'development' && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-200">
          <span>ℹ️</span>
          <span>Showing mock settings. Connect backend to persist changes.</span>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex gap-2">
          <button
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            onClick={() => setShowExportModal(true)}
          >
            <Download className="w-4 h-4 inline mr-1" />
            Export
          </button>
          <button
            className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            onClick={() => setShowImportModal(true)}
          >
            <Upload className="w-4 h-4 inline mr-1" />
            Import
          </button>
          <button
            className="px-3 py-1.5 text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50"
            onClick={() => handleReset()}
            disabled={resetMutation.isPending}
          >
            {resetMutation.isPending ? (
              <RotateCw className="w-4 h-4 inline mr-1 animate-spin" />
            ) : (
              <RotateCw className="w-4 h-4 inline mr-1" />
            )}
            Reset All
          </button>
        </div>
      </div>

      {/* Last Updated Info */}
      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm text-gray-600 dark:text-gray-400">
        <strong className="text-gray-900 dark:text-white">Last Updated:</strong> {new Date(settings.lastUpdated).toLocaleString()} by {settings.updatedBy}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b-2 border-gray-200 dark:border-gray-700">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400 -mb-[2px]'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        {activeTab === 'theme' && (
          <ThemeTab
            theme={settings.theme}
            onUpdate={handleUpdateTheme}
            onReset={() => handleReset('theme')}
            isUpdating={updateThemeMutation.isPending}
            isResetting={resetMutation.isPending}
          />
        )}

        {activeTab === 'display' && (
          <DisplayTab
            display={settings.display}
            onUpdate={handleUpdateDisplay}
            onReset={() => handleReset('display')}
            isUpdating={updateDisplayMutation.isPending}
            isResetting={resetMutation.isPending}
          />
        )}

        {activeTab === 'notifications' && (
          <NotificationsTab
            notifications={settings.notifications}
            onUpdate={handleUpdateNotifications}
            onReset={() => handleReset('notifications')}
            isUpdating={updateNotificationsMutation.isPending}
            isResetting={resetMutation.isPending}
          />
        )}

        {activeTab === 'security' && (
          <SecurityTab
            security={settings.security}
            onUpdate={handleUpdateSecurity}
            onReset={() => handleReset('security')}
            isUpdating={updateSecurityMutation.isPending}
            isResetting={resetMutation.isPending}
          />
        )}

        {activeTab === 'system' && (
          <SystemTab
            system={settings.system}
            onUpdate={handleUpdateSystem}
            onReset={() => handleReset('system')}
            isUpdating={updateSystemMutation.isPending}
            isResetting={resetMutation.isPending}
          />
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={exportMutation.isPending}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onImport={handleImport}
          isImporting={importMutation.isPending}
          error={importMutation.error as Error}
        />
      )}
    </div>
  );
};

export default ThemeSettingsManager;
